<template>
  <div class="physical-examination-form">
    <!-- Header con título y controles -->
    <div class="examination-header">
      <div class="header-title">
        <i class="fas fa-heartbeat"></i>
        <h5>Exploración Física Detallada</h5>
      </div>
      <div class="header-controls">
        <button
class="unit-toggle-btn" :title="toggleButtonLabel" type="button"
          @click.prevent.stop="handleToggleUnits">
          <i class="fas fa-exchange-alt"></i>
          {{ currentUnitLabel }}
        </button>
      </div>
    </div>

    <!-- Formulario Grid Compacto -->
    <div class="examination-grid-compact">
      <!-- Signos Vitales Compactos -->
      <div class="section-group-compact">
        <div class="section-title-compact">
          <i class="fas fa-heartbeat"></i>
          <span>Signos Vitales</span>
        </div>

        <div class="fields-row">
          <!-- Presión Arterial -->
          <div class="field-compact blood-pressure-compact">
            <label>
              <i class="fas fa-tint"></i>
              PA
            </label>
            <div class="bp-inputs-compact">
              <input
v-model="localData.systolic" type="number" step="1" min="0" max="300" placeholder="SYS"
                class="form-input-compact" />
              <span class="separator-compact">/</span>
              <input
v-model="localData.diastolic" type="number" step="1" min="0" max="200" placeholder="DIA"
                class="form-input-compact" />
            </div>
            <span class="unit-compact">mmHg</span>
          </div>

          <!-- FC -->
          <div class="field-compact">
            <label>
              <i class="fas fa-heart"></i>
              FC
            </label>
            <input
v-model="localData.heart_rate" type="number" step="1" min="0" max="300" placeholder="bpm"
              class="form-input-compact" />
            <span class="unit-compact">bpm</span>
          </div>

          <!-- FR -->
          <div class="field-compact">
            <label>
              <i class="fas fa-lungs"></i>
              FR
            </label>
            <input
v-model="localData.respiratory_rate" type="number" step="1" min="0" max="100" placeholder="rpm"
              class="form-input-compact" />
            <span class="unit-compact">rpm</span>
          </div>

          <!-- Temperatura -->
          <div class="field-compact">
            <label>
              <i class="fas fa-thermometer-half"></i>
              Temp
            </label>
            <input
v-model="localData.temperature" type="number" step="0.1" min="30" max="45" placeholder="°C"
              class="form-input-compact" />
            <span class="unit-compact">°C</span>
          </div>

          <!-- SaO2 -->
          <div class="field-compact">
            <label>
              <i class="fas fa-wind"></i>
              SaO₂
            </label>
            <input
v-model="localData.oxygen_saturation" type="number" step="1" min="0" max="100" placeholder="%"
              class="form-input-compact" />
            <span class="unit-compact">%</span>
          </div>
        </div>
      </div>

      <!-- Antropometría Compacta -->
      <div class="section-group-compact">
        <div class="section-title-compact">
          <i class="fas fa-ruler-combined"></i>
          <span>Antropometría</span>
        </div>

        <div class="fields-row">
          <!-- Peso -->
          <div class="field-compact weight-field">
            <label>
              <i class="fas fa-weight"></i>
              Peso
              <span class="unit-badge-compact">{{ currentUnitLabel }}</span>
            </label>
            <input
v-model="localData.weight" type="text" :placeholder="weightPlaceholder" class="form-input-compact"
              @input="handleWeightInput" />
            <span class="unit-compact">{{ currentUnitLabel }}</span>
            <div v-if="weightInOtherUnit" class="conversion-hint-compact">
              ≈ {{ weightInOtherUnit }}
            </div>
          </div>

          <!-- Talla -->
          <div class="field-compact">
            <label>
              <i class="fas fa-ruler-vertical"></i>
              Talla
            </label>
            <input
v-model="localData.height" type="text" placeholder="m.cm" class="form-input-compact"
              @input="handleHeightInput" />
            <span class="unit-compact">m</span>
          </div>

          <!-- IMC -->
          <div class="field-compact imc-field-compact">
            <label>
              <i class="fas fa-calculator"></i>
              IMC
            </label>
            <input
:value="bmiValue" type="text" readonly class="form-input-compact imc-display-compact"
              :class="bmiStatusClass" />
            <span class="unit-compact">kg/m²</span>
            <div v-if="bmiCategory" :class="['bmi-indicator-compact', bmiCategory.class]">
              <i :class="bmiCategory.icon"></i>
            </div>
          </div>

          <!-- Cintura -->
          <div class="field-compact">
            <label>
              <i class="fas fa-circle"></i>
              Cintura
            </label>
            <input
v-model="localData.waist" type="number" step="0.1" min="0" placeholder="cm"
              class="form-input-compact" />
            <span class="unit-compact">cm</span>
          </div>

          <!-- Cadera -->
          <div class="field-compact">
            <label>
              <i class="fas fa-circle"></i>
              Cadera
            </label>
            <input
v-model="localData.hip" type="number" step="0.1" min="0" placeholder="cm"
              class="form-input-compact" />
            <span class="unit-compact">cm</span>
          </div>

          <!-- ICC -->
          <div v-if="waistHipRatio" class="field-compact">
            <label>
              <i class="fas fa-percentage"></i>
              ICC
            </label>
            <input :value="waistHipRatio" type="text" readonly class="form-input-compact" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useWeightUnits } from '@/composables/useWeightUnits';

const props = defineProps({
  modelValue: {
    type: String,
    default: '{}'
  }
});

const emit = defineEmits(['update:modelValue']);

// Composable de unidades de peso
const {
  isKg,
  currentUnitLabel,
  toggleButtonLabel,
  weightPlaceholder,
  toggleUnit,
  parseKgWeight,
  formatKgWeight,
  kgToLb,
  lbToKg,
  convertWeight
} = useWeightUnits();

// Parsear modelValue JSON
const parseModelValue = (value) => {
  try {
    return value ? JSON.parse(value) : {};
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return {};
  }
};

// Estado local del formulario
const localData = ref(parseModelValue(props.modelValue));

// Manejo de cambio de unidades
const handleToggleUnits = () => {
  const currentWeight = localData.value.weight;
  if (currentWeight) {
    const fromUnit = isKg.value ? 'kg' : 'lb';
    localData.value.weight = convertWeight(currentWeight, fromUnit);
  }
  toggleUnit();
};

// Validación y formateo de peso
const handleWeightInput = (event) => {
  let value = event.target.value;

  if (isKg.value) {
    // Formato kg.g - permitir números y un punto
    value = value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
  } else {
    // Formato lb - permitir números y decimales
    value = value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    // Limitar a 2 decimales
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }
  }

  localData.value.weight = value;
};

// Validación y formateo de altura
const handleHeightInput = (event) => {
  let value = event.target.value;
  // Formato m.cm - permitir números y un punto
  value = value.replace(/[^0-9.]/g, '');
  const parts = value.split('.');
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('');
  }
  localData.value.height = value;
};

// Cálculo del IMC
const bmiValue = computed(() => {
  const weight = localData.value.weight;
  const height = localData.value.height;

  if (!weight || !height) return '';

  // Convertir peso a kg si está en lb
  let weightKg;
  if (isKg.value) {
    weightKg = parseKgWeight(weight);
  } else {
    weightKg = lbToKg(parseFloat(weight));
  }

  // Parsear altura (formato m.cm)
  const [meters, cm] = String(height).split('.').map(Number);
  const totalHeight = meters + ((cm || 0) / 100);

  if (!weightKg || !totalHeight || totalHeight === 0) return '';

  const bmi = weightKg / (totalHeight * totalHeight);
  return bmi.toFixed(2);
});

// Categoría del IMC
const bmiCategory = computed(() => {
  const bmi = parseFloat(bmiValue.value);
  if (!bmi) return null;

  if (bmi < 18.5) {
    return {
      text: 'Bajo peso',
      class: 'underweight',
      icon: 'fas fa-arrow-down'
    };
  } else if (bmi < 25) {
    return {
      text: 'Normal',
      class: 'normal',
      icon: 'fas fa-check'
    };
  } else if (bmi < 30) {
    return {
      text: 'Sobrepeso',
      class: 'overweight',
      icon: 'fas fa-exclamation-triangle'
    };
  } else {
    return {
      text: 'Obesidad',
      class: 'obese',
      icon: 'fas fa-exclamation-circle'
    };
  }
});

const bmiStatusClass = computed(() => {
  return bmiCategory.value ? `bmi-${bmiCategory.value.class}` : '';
});

// Conversión de peso en tiempo real
const weightInOtherUnit = computed(() => {
  const weight = localData.value.weight;
  if (!weight) return '';

  if (isKg.value) {
    const totalKg = parseKgWeight(weight);
    const lb = kgToLb(totalKg);
    return `${lb.toFixed(2)} Lb`;
  } else {
    const lb = parseFloat(weight);
    const totalKg = lbToKg(lb);
    return formatKgWeight(totalKg) + ' Kg';
  }
});

// Índice cintura-cadera
const waistHipRatio = computed(() => {
  const waist = parseFloat(localData.value.waist);
  const hip = parseFloat(localData.value.hip);

  if (!waist || !hip || hip === 0) return '';

  return (waist / hip).toFixed(2);
});

// Watchers para sincronización
watch(() => props.modelValue, (newValue) => {
  localData.value = parseModelValue(newValue);
}, { immediate: true });

watch(localData, (newData) => {
  // Agregar IMC calculado
  newData.bmi = bmiValue.value;
  emit('update:modelValue', JSON.stringify(newData));
}, { deep: true });

</script>


<style scoped>
.physical-examination-form {
  background: #ffffff;
  border-radius: 12px;
  padding: 1rem;
}

/* Header Compacto */
.examination-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #f0f2f5;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.header-title i {
  font-size: 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-title h5 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #344767;
}

.header-controls {
  display: flex;
  gap: 0.5rem;
}

.unit-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}

.unit-toggle-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(102, 126, 234, 0.4);
}

.unit-toggle-btn i {
  font-size: 0.75rem;
}

/* Grid Compacto */
.examination-grid-compact {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Section Groups Compactos */
.section-group-compact {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-title-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-left: 3px solid #667eea;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
}

.section-title-compact i {
  font-size: 0.875rem;
  color: #667eea;
}

/* Fields Row */
.fields-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.75rem;
  align-items: flex-start;
  width: 100%;
}

/* Field Compacto */
.field-compact {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
}

.field-compact.weight-field {
  flex: 1.2;
}

.field-compact.blood-pressure-compact {
  flex: 1.5;
}

.field-compact label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #344767;
  margin: 0;
}

.field-compact label i {
  font-size: 0.75rem;
  color: #667eea;
  width: 12px;
  text-align: center;
}

.unit-badge-compact {
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.375rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 700;
  margin-left: 0.25rem;
}

/* Input Compacto */
.form-input-compact {
  width: 100%;
  padding: 0.5rem 0.625rem;
  padding-right: 2.5rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #344767;
  transition: all 0.3s ease;
  background: white;
}

.form-input-compact:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.form-input-compact:read-only {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  cursor: not-allowed;
}

.unit-compact {
  position: absolute;
  right: 0.625rem;
  bottom: 2.125rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #8392ab;
  pointer-events: none;
}

/* Blood Pressure Compacto */
.bp-inputs-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bp-inputs-compact input {
  flex: 1;
  min-width: 0;
}

.separator-compact {
  font-size: 1.125rem;
  font-weight: 700;
  color: #8392ab;
  margin: 0 -0.25rem;
}

/* IMC Field Compacto */
.imc-field-compact .imc-display-compact {
  font-weight: 700;
  font-size: 0.875rem;
  text-align: center;
  padding-right: 0.625rem;
}

.bmi-underweight {
  border-color: #0d6efd !important;
  background: #e7f1ff !important;
  color: #084298 !important;
}

.bmi-normal {
  border-color: #28a745 !important;
  background: #d4edda !important;
  color: #155724 !important;
}

.bmi-overweight {
  border-color: #ffc107 !important;
  background: #fff8e1 !important;
  color: #856404 !important;
}

.bmi-obese {
  border-color: #dc3545 !important;
  background: #fce8e8 !important;
  color: #721c24 !important;
}

.bmi-indicator-compact {
  position: absolute;
  top: 1.5rem;
  right: 0.5rem;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.625rem;
}

.bmi-indicator-compact.underweight {
  background: #0d6efd;
  color: white;
}

.bmi-indicator-compact.normal {
  background: #28a745;
  color: white;
}

.bmi-indicator-compact.overweight {
  background: #ffc107;
  color: white;
}

.bmi-indicator-compact.obese {
  background: #dc3545;
  color: white;
}

/* Conversion Hint Compacto */
.conversion-hint-compact {
  font-size: 0.6875rem;
  color: #667eea;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, #f0f3ff 0%, #e8ebff 100%);
  border-radius: 4px;
  border-left: 2px solid #667eea;
  margin-top: -0.125rem;
}

/* Responsive */
@media (max-width: 768px) {
  .fields-row {
    flex-wrap: wrap;
  }

  .field-compact,
  .field-compact.weight-field,
  .field-compact.blood-pressure-compact {
    flex: 1 1 45%;
    min-width: 140px;
  }
}

@media (max-width: 480px) {
  .fields-row {
    flex-direction: column;
  }

  .field-compact,
  .field-compact.weight-field,
  .field-compact.blood-pressure-compact {
    flex: 1;
    width: 100%;
    min-width: 100%;
  }
}

/* Number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  opacity: 0.5;
}

input[type="number"]:hover::-webkit-inner-spin-button,
input[type="number"]:hover::-webkit-outer-spin-button {
  opacity: 1;
}
</style>
